import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Checkbox,
  ListItemText,
} from '@mui/material';

import {
  getRegions,
  updateRegion,
  deleteRegion,
  getAdminsInRegion,
  getPossibleParentRegions,
  createRegion,
  getAllAdminUsers,
} from '../../services/regions.services';
import { States } from '../../types/auth.types';
import { RegionStatus } from '../../types/fair.types';
import { usersService } from '../../services/users.service';
import { Trash2, Pencil, Plus } from 'lucide-react';

// Define interfaces for region and admin user
interface Region {
  id: string;
  name: string;
  location: string;
  regionDirectorId: string;
  state: string;
  status: RegionStatus;
  maxAmountOfFairs: number;
  isPublic: boolean;
  parentRegionId?: string | null;
}

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  regionId: string;
}

const RegionManagement: React.FC = () => {
  // Region Detail Hooks
  const [regionName, setRegionName] = useState('');
  const [regionLocation, setRegionLocation] = useState('');
  const [regionState, setRegionState] = useState('');
  const [regionDirectorId, setRegionDirectorId] = useState('');
  const [regionMaxFairs, setRegionMaxFairs] = useState<string | number>('');
  const [regionStatus, setRegionStatus] = useState<RegionStatus>('pending');
  const [regionAdmins, setRegionAdmins] = useState<AdminUser[]>([]);
  const [allAdminUsers, setAllAdminUsers] = useState<AdminUser[]>([]);
  const [parentRegions, setParentRegions] = useState<Region[]>([]);
  const [parentRegionId, setParentRegionId] = useState<string>('None');
  const [isPublic, setIsPublic] = useState(false);
  // Region Dialog Hooks and States
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [userMap, setUserMap] = useState<Record<string, any>>({});
  const [regionToEdit, setRegionToEdit] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Filter Hooks
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchRegions();
  }, []);

  // Fetch regions from the database and insert into a data array for the table
  const fetchRegions = async () => {
    try {
      const rawData = await getRegions();
      console.log('Fetched regions:', rawData);

      // Map the raw data to the Region interface
      const data: Region[] = rawData.map((region: any) => ({
        id: region.id,
        name: region.name,
        location: region.location,
        fairIds: region.fairIds,
        regionDirectorId: region.regionDirectorId,
        state: region.state,
        status: region.status,
        maxAmountOfFairs: region.maxAmountOfFairs,
        isPublic: region.isPublic,
        parentRegionId: region.parentRegionId,
      }));

      setRegions(data);

      // Collect all region director IDs
      const directorIds = [
        ...Array.from(new Set(data.map((region) => region.regionDirectorId))),
      ];

      // Fetch user data (name, email, etc.) for each region director
      const userDataMap: Record<string, any> = {};
      for (const directorId of directorIds) {
        if (directorId) {
          try {
            const userData = await usersService.getUserAccount(directorId);
            userDataMap[directorId] = userData;
          } catch (err) {
            console.error(`Error fetching user for ID ${directorId}:`, err);
          }
        }
      }
      setUserMap(userDataMap);
    } catch (err) {
      console.error('Error fetching regions:', err);
      setError('Failed to load regions');
    }
  };

  // Filter regions based on search term, state, and status
  const filteredRegions = regions.filter((region) => {
    // Filter by the search bar for region name or location
    const matchesSearch =
      region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      region.location.toLowerCase().includes(searchTerm.toLowerCase());
    // Filter by US state
    const matchesState = stateFilter === 'all' || region.state === stateFilter;
    // Filter by Region Status
    const matchesStatus =
      statusFilter === 'all' || region.status === statusFilter;

    return matchesSearch && matchesState && matchesStatus;
  });

  // When the super admin changes the max fairs allowed, take the value and ensure it is a number
  const handleMaxFairsChange = (e: { target: { value: any } }) => {
    const inputValue = e.target.value;
    const parsedValue = parseInt(inputValue, 10);

    if (isNaN(parsedValue) && inputValue !== '') {
      // If not a number, and not empty, do not update the value.
      return;
    }

    if (inputValue === '') {
      // If empty input
      setRegionMaxFairs('');
    } else {
      setRegionMaxFairs(parsedValue);
    }
  };

  // Get the color of the chip based on the status value
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Fetch all admins in the region if we want to change the region director
  const fetchAdminsInRegion = async (regionId: string) => {
    // Check if regionId is valid
    if (!regionId) return;

    try {
      // Get all admins in the region
      const admins = await getAdminsInRegion(regionId);
      console.log('Fetched admins for region:', admins);

      // If there are no admins, set the region admins to an empty array
      if (!admins || admins.length === 0) {
        console.log('No admins found for this region.');
        setRegionAdmins([]);
        return;
      }

      // Set the admins returned in an array
      setRegionAdmins(admins);
    } catch (err) {
      console.error('Error fetching region admins:', err);
      setError('Failed to load region administrators');
    }
  };

  const fetchPossibleParentRegions = async (regionId: string) => {
    try {
      // Get all possible parent regions
      const possibleRegions = await getPossibleParentRegions(regionId);
      console.log('Fetched possible parent regions:', possibleRegions);

      // If there are no possible regions, set the parent regions to an empty array
      if (!possibleRegions || possibleRegions.length === 0) {
        console.log('No possible parent regions found for this region.');
        setParentRegions([]);
        return;
      }
      // Set the possible regions returned in an array
      setParentRegions(possibleRegions);
    } catch (err) {
      console.error('Error fetching possible parent regions:', err);
      setError('Failed to load possible parent regions');
    }
  };

  // Open the edit dialog and set corresponding values to view/edit
  const handleOpenEditDialog = async (regionId: string) => {
    setError(''); // Reset error message
    console.log('Opening edit dialog for region:', regionId);

    try {
      // Get the region data for the selected region
      const regionData = regions.find((region) => region.id === regionId);

      if (!regionData) {
        console.error('Region not found:', regionId);
        setError('Region not found');
        return;
      }

      console.log('Selected region data:', regionData);

      // Set the region to edit first to ensure it's available for the fetch functions
      setRegionToEdit(regionId);

      // Fetch admins for the selected region
      await fetchAdminsInRegion(regionId);

      // Fetch possible parent regions
      await fetchPossibleParentRegions(regionId);

      // Set the region data to the variables
      setRegionName(regionData.name || '');
      setRegionLocation(regionData.location || '');
      setRegionState(regionData.state || '');
      setRegionStatus((regionData.status as RegionStatus) || 'pending');
      setRegionMaxFairs(regionData.maxAmountOfFairs || '');
      setRegionDirectorId(regionData.regionDirectorId || '');
      setParentRegionId(regionData.parentRegionId || '');
      setIsPublic(regionData.isPublic || false);

      // Open the dialog
      setEditDialogOpen(true);
    } catch (err) {
      console.error('Error preparing edit dialog:', err);
      setError('Failed to prepare region for editing');
    }
  };

  // If super admin makes changes and saves, update the region with the new data
  const handleEditRegion = async () => {
    if (isSubmitting) return; // Prevent double submission

    if (!regionToEdit) {
      console.error('No region selected for editing');
      setError('No region selected for editing');
      return;
    }

    setIsSubmitting(true);
    setError(''); // Clear any previous errors

    try {
      // Create a new object with the updated data
      const updatedData = {
        name: regionName,
        location: regionLocation,
        state: regionState,
        status: regionStatus,
        maxAmountOfFairs: Number(regionMaxFairs),
        regionDirectorId: regionDirectorId,
        isPublic: isPublic,
        parentRegionId: parentRegionId || null, // Send null if empty string
      };

      console.log('Updating region with ID:', regionToEdit);
      console.log('Update data:', updatedData);

      // Update the region with new data
      await updateRegion(regionToEdit, updatedData);
      console.log('Region updated successfully');

      // Refresh the region list to update the table on screen
      await fetchRegions();

      // Close the edit screen dialog and reset state
      setEditDialogOpen(false);
      resetFormState();
    } catch (err) {
      console.error('Error updating region:', err);
      setError(
        'Failed to update region: ' +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form state after closing dialog
  const resetFormState = () => {
    setRegionName('');
    setRegionLocation('');
    setRegionState('');
    setRegionStatus('pending');
    setRegionMaxFairs('');
    setRegionDirectorId('');
    setParentRegionId('');
    setIsPublic(false);
    setRegionToEdit(null);
    setRegionAdmins([]);
    setAllAdminUsers([]);
    setParentRegions([]);
  };

  // Fetch all admin users for region director selection
  const fetchAllAdminUsers = async () => {
    try {
      const admins = await getAllAdminUsers();
      console.log('Fetched all admin users:', admins);
      setAllAdminUsers(admins);
    } catch (err) {
      console.error('Error fetching all admin users:', err);
      setError('Failed to load admin users');
    }
  };

  // Open the create dialog
  const handleOpenCreateDialog = async () => {
    setError(''); // Reset error message
    console.log('Opening create dialog for new region');

    try {
      // Reset form state
      resetFormState();

      // Fetch all admin users for region director selection
      await fetchAllAdminUsers();

      // Fetch possible parent regions for new region
      await fetchPossibleParentRegions('');

      // Open the dialog
      setCreateDialogOpen(true);
    } catch (err) {
      console.error('Error preparing create dialog:', err);
      setError('Failed to prepare region creation form');
    }
  };

  // Handle creating a new region
  const handleCreateRegion = async () => {
    if (isSubmitting) return; // Prevent double submission

    setIsSubmitting(true);
    setError(''); // Clear any previous errors

    try {
      // Validate required fields
      if (!regionName.trim()) {
        setError('Region name is required');
        return;
      }
      if (!regionLocation.trim()) {
        setError('Location is required');
        return;
      }
      if (!regionState) {
        setError('State is required');
        return;
      }
      if (!regionDirectorId) {
        setError('Region Director is required');
        return;
      }

      // Create the region data object
      const regionData = {
        name: regionName.trim(),
        location: regionLocation.trim(),
        regionDirectorId: regionDirectorId,
        state: regionState,
        status: regionStatus,
        maxAmountOfFairs: Number(regionMaxFairs) || 5,
        isPublic: isPublic,
        parentRegionId: parentRegionId || null,
      };

      console.log('Creating region with data:', regionData);

      // Create the region with all fields
      await createRegion(
        regionData.name,
        regionData.location,
        regionData.regionDirectorId,
        regionData.state,
        regionData.status,
        regionData.maxAmountOfFairs,
        regionData.isPublic,
        regionData.parentRegionId
      );

      console.log('Region created successfully');

      // Refresh the region list to update the table on screen
      await fetchRegions();

      // Close the create dialog and reset state
      setCreateDialogOpen(false);
      resetFormState();
    } catch (err) {
      console.error('Error creating region:', err);
      setError(
        'Failed to create region: ' +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle dialog close with proper state cleanup
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    resetFormState();
  };

  // Handle dialog close with proper state cleanup
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    resetFormState();
  };

  // Open the delete confirmation dialog
  const handleOpenDeleteDialog = (regionId: string) => {
    setRegionToEdit(regionId);
    setDeleteDialogOpen(true);
  };

  // Deletion of region
  const handleDeleteRegion = async () => {
    // Check if regionId is valid
    if (!regionToEdit) return;

    try {
      // Delete the region with the selected ID
      console.log('Deleting region:', regionToEdit);
      await deleteRegion(regionToEdit);

      // Refresh the region list to update the table on screen
      await fetchRegions();

      // Close the delete confirmation dialog and reset the region to edit
      setDeleteDialogOpen(false);
      setRegionToEdit(null);
    } catch (err) {
      console.error('Error deleting region:', err);
      setError('Failed to delete region');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Region Management
      </Typography>

      {/* Filters section */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{ display: 'flex', gap: 2, alignItems: 'center', flexGrow: 1 }}
          >
            <TextField
              size="small"
              label="Search regions"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>State</InputLabel>
              <Select
                value={stateFilter}
                label="State"
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <MenuItem value="all">All States</MenuItem>
                {Array.from(new Set(regions.map((region) => region.state))).map(
                  (state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleOpenCreateDialog}
            sx={{ ml: 2 }}
          >
            Create Region
          </Button>
        </Box>
      </Paper>

      {/* Display any global errors */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Region Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Region Name</TableCell>
              <TableCell>Region Director</TableCell>
              <TableCell>Director Email</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRegions.length > 0 ? (
              filteredRegions.map((region) => (
                <TableRow key={region.id}>
                  <TableCell
                    sx={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {region.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {region.regionDirectorId && userMap[region.regionDirectorId]
                      ? userMap[region.regionDirectorId].firstName +
                        ' ' +
                        userMap[region.regionDirectorId].lastName
                      : region.regionDirectorId}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {region.regionDirectorId && userMap[region.regionDirectorId]
                      ? userMap[region.regionDirectorId].email
                      : region.regionDirectorId}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {region.location}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {region.state}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={region.status}
                      color={getStatusChipColor(region.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {/* Edit Button */}
                    <IconButton onClick={() => handleOpenEditDialog(region.id)}>
                      <Pencil size={20} />
                    </IconButton>
                    {/* Delete Button */}
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(region.id)}
                    >
                      <Trash2 size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No regions found matching your filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Region Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Region</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Region Name"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
          />
          <TextField
            label="Location"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionLocation}
            onChange={(e) => setRegionLocation(e.target.value)}
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel id="state-select-label">State</InputLabel>
            <Select
              labelId="state-select-label"
              value={regionState}
              onChange={(e) => setRegionState(e.target.value)}
              label="State"
            >
              {Array.isArray(States) &&
                States.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={regionStatus}
              label="Status"
              onChange={(e) => setRegionStatus(e.target.value as RegionStatus)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Max Amount of Fairs Allowed"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionMaxFairs}
            onChange={handleMaxFairsChange}
            type="number"
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel id="director-select-label">Region Director</InputLabel>
            <Select
              labelId="director-select-label"
              value={regionDirectorId}
              onChange={(e) => setRegionDirectorId(e.target.value)}
              label="Region Director"
            >
              {regionAdmins.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.firstName} {admin.lastName} ({admin.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel id="parentRegion-select-label">
              Parent Region
            </InputLabel>
            <Select
              labelId="parentRegion-select-label"
              value={parentRegionId}
              onChange={(e) => setParentRegionId(e.target.value)}
              label="Parent Region"
              displayEmpty
            >
              <MenuItem value="">None</MenuItem>
              {parentRegions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name} - {region.location} ({region.state})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" component="fieldset">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={isPublic}
                onChange={(event) => setIsPublic(event.target.checked)}
                name="isPublic"
              />
              <ListItemText primary="Public Region" />
            </div>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleEditRegion}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Region Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Region</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Region Name"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
            required
          />
          <TextField
            label="Location"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionLocation}
            onChange={(e) => setRegionLocation(e.target.value)}
            required
          />
          <FormControl fullWidth variant="outlined" margin="dense" required>
            <InputLabel id="create-state-select-label">State</InputLabel>
            <Select
              labelId="create-state-select-label"
              value={regionState}
              onChange={(e) => setRegionState(e.target.value)}
              label="State"
            >
              {Array.isArray(States) &&
                States.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="create-status-select-label">Status</InputLabel>
            <Select
              labelId="create-status-select-label"
              id="create-status-select"
              value={regionStatus}
              label="Status"
              onChange={(e) => setRegionStatus(e.target.value as RegionStatus)}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Max Amount of Fairs Allowed"
            fullWidth
            variant="outlined"
            margin="dense"
            value={regionMaxFairs}
            onChange={handleMaxFairsChange}
            type="number"
            placeholder="5"
          />
          <FormControl fullWidth variant="outlined" margin="dense" required>
            <InputLabel id="create-director-select-label">
              Region Director
            </InputLabel>
            <Select
              labelId="create-director-select-label"
              value={regionDirectorId}
              onChange={(e) => setRegionDirectorId(e.target.value)}
              label="Region Director"
            >
              {allAdminUsers.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.firstName} {admin.lastName} ({admin.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel id="create-parentRegion-select-label">
              Parent Region
            </InputLabel>
            <Select
              labelId="create-parentRegion-select-label"
              value={parentRegionId}
              onChange={(e) => setParentRegionId(e.target.value)}
              label="Parent Region"
              displayEmpty
            >
              <MenuItem value="">None</MenuItem>
              {parentRegions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name} - {region.location} ({region.state})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" component="fieldset">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={isPublic}
                onChange={(event) => setIsPublic(event.target.checked)}
                name="isPublic"
              />
              <ListItemText primary="Public Region" />
            </div>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateRegion}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Region'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this region?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteRegion}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegionManagement;
